import React, { useState, useEffect } from 'react';
import { API } from '../App';
import {
  Factory, Droplets, Building2, Leaf, TrendingUp, Shield, FileText,
  ChevronRight, CheckCircle, AlertTriangle, Info, Loader, BarChart3,
  Globe, Flame, Users, Heart, Truck, Package, Crown, Sparkles, Lock, Car, Zap, Download, Shirt, Monitor
} from 'lucide-react';
import { PremiumGate } from '../components/PremiumGate';
import { apiFetch } from '../services/api_service';


// GRI Sector Standards Data
const GRI_SECTOR_STANDARDS = {
  oil_gas: {
    id: 'gri_11',
    name: 'Oil and Gas',
    icon: Flame,
    color: 'amber',
    description: 'GRI 11: Oil and Gas Sector 2021',
    topics: [
      { id: '11.1', name: 'GHG emissions', priority: 'critical', esrs: 'E1' },
      { id: '11.2', name: 'Climate adaptation & resilience', priority: 'high', esrs: 'E1' },
      { id: '11.3', name: 'Air emissions', priority: 'high', esrs: 'E2' },
      { id: '11.4', name: 'Biodiversity', priority: 'high', esrs: 'E4' },
      { id: '11.5', name: 'Waste', priority: 'medium', esrs: 'E5' },
      { id: '11.6', name: 'Water & effluents', priority: 'high', esrs: 'E3' },
      { id: '11.7', name: 'Closure & rehabilitation', priority: 'medium', esrs: 'E4' },
      { id: '11.8', name: 'Asset integrity & critical incident management', priority: 'critical', esrs: 'S1' },
      { id: '11.9', name: 'Occupational health & safety', priority: 'critical', esrs: 'S1' },
      { id: '11.10', name: 'Employment practices', priority: 'high', esrs: 'S1' },
      { id: '11.11', name: 'Non-discrimination & equal opportunity', priority: 'medium', esrs: 'S1' },
      { id: '11.12', name: 'Forced labor & modern slavery', priority: 'critical', esrs: 'S2' },
      { id: '11.13', name: 'Freedom of association', priority: 'high', esrs: 'S1' },
      { id: '11.14', name: 'Economic impacts', priority: 'medium', esrs: 'G1' },
      { id: '11.15', name: 'Local communities', priority: 'high', esrs: 'S3' },
      { id: '11.16', name: 'Land & resource rights', priority: 'critical', esrs: 'S3' },
      { id: '11.17', name: 'Rights of indigenous peoples', priority: 'critical', esrs: 'S3' },
      { id: '11.18', name: 'Conflict & security', priority: 'high', esrs: 'G1' },
      { id: '11.19', name: 'Anti-competitive behavior', priority: 'medium', esrs: 'G1' },
      { id: '11.20', name: 'Anti-corruption', priority: 'high', esrs: 'G1' },
      { id: '11.21', name: 'Payments to governments', priority: 'medium', esrs: 'G1' },
      { id: '11.22', name: 'Public policy', priority: 'medium', esrs: 'G1' }
    ]
  },
  coal: {
    id: 'gri_12',
    name: 'Coal',
    icon: Factory,
    color: 'slate',
    description: 'GRI 12: Coal Sector 2022',
    topics: [
      { id: '12.1', name: 'GHG emissions', priority: 'critical', esrs: 'E1' },
      { id: '12.2', name: 'Climate adaptation', priority: 'critical', esrs: 'E1' },
      { id: '12.3', name: 'Air emissions', priority: 'high', esrs: 'E2' },
      { id: '12.4', name: 'Biodiversity', priority: 'high', esrs: 'E4' },
      { id: '12.5', name: 'Waste', priority: 'high', esrs: 'E5' },
      { id: '12.6', name: 'Water & effluents', priority: 'high', esrs: 'E3' },
      { id: '12.7', name: 'Closure & rehabilitation', priority: 'critical', esrs: 'E4' },
      { id: '12.8', name: 'Occupational health & safety', priority: 'critical', esrs: 'S1' },
      { id: '12.9', name: 'Employment practices', priority: 'high', esrs: 'S1' },
      { id: '12.10', name: 'Forced labor', priority: 'critical', esrs: 'S2' },
      { id: '12.11', name: 'Local communities', priority: 'high', esrs: 'S3' },
      { id: '12.12', name: 'Land & resource rights', priority: 'critical', esrs: 'S3' },
      { id: '12.13', name: 'Rights of indigenous peoples', priority: 'critical', esrs: 'S3' },
      { id: '12.14', name: 'Anti-corruption', priority: 'high', esrs: 'G1' }
    ]
  },
  agriculture: {
    id: 'gri_13',
    name: 'Agriculture, Aquaculture & Fishing',
    icon: Leaf,
    color: 'green',
    description: 'GRI 13: Agriculture, Aquaculture and Fishing Sectors 2022',
    topics: [
      { id: '13.1', name: 'GHG emissions', priority: 'high', esrs: 'E1' },
      { id: '13.2', name: 'Climate adaptation', priority: 'high', esrs: 'E1' },
      { id: '13.3', name: 'Biodiversity', priority: 'critical', esrs: 'E4' },
      { id: '13.4', name: 'Natural ecosystem conversion', priority: 'critical', esrs: 'E4' },
      { id: '13.5', name: 'Soil health', priority: 'high', esrs: 'E4' },
      { id: '13.6', name: 'Pesticides use', priority: 'high', esrs: 'E2' },
      { id: '13.7', name: 'Water & effluents', priority: 'high', esrs: 'E3' },
      { id: '13.8', name: 'Waste', priority: 'medium', esrs: 'E5' },
      { id: '13.9', name: 'Food security', priority: 'high', esrs: 'S3' },
      { id: '13.10', name: 'Food safety', priority: 'critical', esrs: 'S4' },
      { id: '13.11', name: 'Animal health & welfare', priority: 'high', esrs: 'E4' },
      { id: '13.12', name: 'Local communities', priority: 'high', esrs: 'S3' },
      { id: '13.13', name: 'Land & resource rights', priority: 'critical', esrs: 'S3' },
      { id: '13.14', name: 'Rights of indigenous peoples', priority: 'critical', esrs: 'S3' },
      { id: '13.15', name: 'Non-discrimination', priority: 'medium', esrs: 'S1' },
      { id: '13.16', name: 'Forced labor', priority: 'critical', esrs: 'S2' },
      { id: '13.17', name: 'Child labor', priority: 'critical', esrs: 'S2' },
      { id: '13.18', name: 'Freedom of association', priority: 'high', esrs: 'S1' },
      { id: '13.19', name: 'Occupational health & safety', priority: 'high', esrs: 'S1' },
      { id: '13.20', name: 'Employment practices', priority: 'high', esrs: 'S1' },
      { id: '13.21', name: 'Living income & living wage', priority: 'high', esrs: 'S1' },
      { id: '13.22', name: 'Economic inclusion', priority: 'medium', esrs: 'S3' },
      { id: '13.23', name: 'Supply chain traceability', priority: 'high', esrs: 'S2' },
      { id: '13.24', name: 'Anti-corruption', priority: 'medium', esrs: 'G1' }
    ]
  },
  mining: {
    id: 'gri_14',
    name: 'Mining',
    icon: Factory,
    color: 'orange',
    description: 'GRI 14: Mining Sector 2024',
    topics: [
      { id: '14.1', name: 'GHG emissions', priority: 'high', esrs: 'E1' },
      { id: '14.2', name: 'Climate adaptation', priority: 'high', esrs: 'E1' },
      { id: '14.3', name: 'Air emissions', priority: 'high', esrs: 'E2' },
      { id: '14.4', name: 'Biodiversity', priority: 'critical', esrs: 'E4' },
      { id: '14.5', name: 'Waste', priority: 'high', esrs: 'E5' },
      { id: '14.6', name: 'Water & effluents', priority: 'critical', esrs: 'E3' },
      { id: '14.7', name: 'Closure & rehabilitation', priority: 'critical', esrs: 'E4' },
      { id: '14.8', name: 'Tailings management', priority: 'critical', esrs: 'E5' },
      { id: '14.9', name: 'Occupational health & safety', priority: 'critical', esrs: 'S1' },
      { id: '14.10', name: 'Employment practices', priority: 'high', esrs: 'S1' },
      { id: '14.11', name: 'Forced labor', priority: 'critical', esrs: 'S2' },
      { id: '14.12', name: 'Child labor', priority: 'critical', esrs: 'S2' },
      { id: '14.13', name: 'Local communities', priority: 'critical', esrs: 'S3' },
      { id: '14.14', name: 'Land & resource rights', priority: 'critical', esrs: 'S3' },
      { id: '14.15', name: 'Rights of indigenous peoples', priority: 'critical', esrs: 'S3' },
      { id: '14.16', name: 'Artisanal & small-scale mining', priority: 'high', esrs: 'S3' },
      { id: '14.17', name: 'Conflict & security', priority: 'high', esrs: 'G1' },
      { id: '14.18', name: 'Anti-corruption', priority: 'high', esrs: 'G1' },
      { id: '14.19', name: 'Payments to governments', priority: 'medium', esrs: 'G1' }
    ]
  },
  financial_services: {
    id: 'gri_fs',
    name: 'Financial Services',
    icon: Building2,
    color: 'blue',
    description: 'GRI Financial Services Sector Supplement',
    topics: [
      { id: 'FS1', name: 'ESG policies for business lines', priority: 'high', esrs: 'G1' },
      { id: 'FS2', name: 'ESG risk assessment procedures', priority: 'high', esrs: 'G1' },
      { id: 'FS3', name: 'Client ESG compliance monitoring', priority: 'medium', esrs: 'S2' },
      { id: 'FS4', name: 'Staff ESG competencies', priority: 'medium', esrs: 'S1' },
      { id: 'FS5', name: 'Client ESG interactions', priority: 'medium', esrs: 'S4' },
      { id: 'FS6', name: 'Portfolio by business line', priority: 'high', esrs: 'G1' },
      { id: 'FS7', name: 'Products with social benefits', priority: 'high', esrs: 'S4' },
      { id: 'FS8', name: 'Products with environmental benefits', priority: 'high', esrs: 'E1' },
      { id: 'FS9', name: 'Coverage & frequency of audits', priority: 'medium', esrs: 'G1' },
      { id: 'FS10', name: 'ESG engagement with investees', priority: 'high', esrs: 'G1' },
      { id: 'FS11', name: 'Assets with positive ESG screening', priority: 'high', esrs: 'G1' },
      { id: 'FS12', name: 'Proxy voting on ESG issues', priority: 'medium', esrs: 'G1' },
      { id: 'FS13', name: 'Access points in underserved areas', priority: 'medium', esrs: 'S3' },
      { id: 'FS14', name: 'Financial literacy initiatives', priority: 'medium', esrs: 'S3' },
      { id: 'FS15', name: 'Fair design of products', priority: 'high', esrs: 'S4' },
      { id: 'FS16', name: 'Financial inclusion initiatives', priority: 'high', esrs: 'S3' }
    ]
  },
  automotive: {
    id: 'gri_auto',
    name: 'Automotive',
    icon: Car,
    color: 'indigo',
    description: 'GRI Automotive Sector Standard 2024 - Electric Vehicle Transition',
    topics: [
      // Climate & Emissions (E1)
      { id: 'AUTO.1', name: 'Fleet CO2 emissions & EU targets', priority: 'critical', esrs: 'E1', 
        detail: 'Vehicle use-phase emissions, fleet average targets (95g CO2/km), compliance with EU 2025-2030 regulations' },
      { id: 'AUTO.2', name: 'Production GHG emissions (Scope 1+2)', priority: 'critical', esrs: 'E1',
        detail: 'Manufacturing facility emissions, energy consumption per vehicle produced' },
      { id: 'AUTO.3', name: 'Supply chain emissions (Scope 3)', priority: 'critical', esrs: 'E1',
        detail: 'Upstream emissions from steel, aluminum, batteries, semiconductors; downstream use-phase and end-of-life' },
      { id: 'AUTO.4', name: 'EV transition strategy', priority: 'critical', esrs: 'E1',
        detail: 'BEV/PHEV production share, ICE phase-out timeline, charging infrastructure investments' },
      { id: 'AUTO.5', name: 'Renewable energy in production', priority: 'high', esrs: 'E1',
        detail: 'Share of renewable electricity, on-site generation, green energy procurement' },
      
      // Pollution & Air Quality (E2)
      { id: 'AUTO.6', name: 'Air emissions from manufacturing', priority: 'high', esrs: 'E2',
        detail: 'VOCs from paint shops, particulate matter, NOx from testing facilities' },
      { id: 'AUTO.7', name: 'Vehicle real-world emissions', priority: 'high', esrs: 'E2',
        detail: 'WLTP vs real-world performance, RDE testing compliance, NOx/PM from diesel vehicles' },
      
      // Water & Resources (E3, E5)
      { id: 'AUTO.8', name: 'Water consumption in production', priority: 'medium', esrs: 'E3',
        detail: 'Water usage per vehicle, wastewater treatment, closed-loop systems' },
      { id: 'AUTO.9', name: 'Circular economy & recycling', priority: 'high', esrs: 'E5',
        detail: 'Recycled content in vehicles, end-of-life vehicle recovery rates, secondary raw materials' },
      { id: 'AUTO.10', name: 'Battery recycling & second life', priority: 'critical', esrs: 'E5',
        detail: 'Li-ion battery recycling rates, second-life applications, raw material recovery' },
      
      // Biodiversity (E4)
      { id: 'AUTO.11', name: 'Land use & biodiversity', priority: 'medium', esrs: 'E4',
        detail: 'Manufacturing site impacts, supplier land use for raw material extraction' },
      
      // Workforce (S1)
      { id: 'AUTO.12', name: 'Just transition for workforce', priority: 'critical', esrs: 'S1',
        detail: 'Reskilling programs for EV transition, ICE workforce impacts, regional employment effects' },
      { id: 'AUTO.13', name: 'Occupational health & safety', priority: 'high', esrs: 'S1',
        detail: 'Factory safety, high-voltage EV handling, ergonomics in assembly' },
      { id: 'AUTO.14', name: 'Employment practices & diversity', priority: 'high', esrs: 'S1',
        detail: 'Workforce diversity, gender pay equity, temporary vs permanent workers' },
      
      // Supply Chain (S2)
      { id: 'AUTO.15', name: 'Battery supply chain due diligence', priority: 'critical', esrs: 'S2',
        detail: 'Cobalt sourcing ethics, lithium extraction impacts, supply chain transparency' },
      { id: 'AUTO.16', name: 'Forced labor in supply chain', priority: 'critical', esrs: 'S2',
        detail: 'Supplier audits, conflict minerals compliance, modern slavery risks' },
      { id: 'AUTO.17', name: 'Semiconductor supply resilience', priority: 'high', esrs: 'S2',
        detail: 'Chip supply security, geographic concentration risks, supplier diversification' },
      
      // Communities (S3)
      { id: 'AUTO.18', name: 'Local community impacts', priority: 'medium', esrs: 'S3',
        detail: 'Plant location employment, community investment, regional economic development' },
      { id: 'AUTO.19', name: 'EV infrastructure access', priority: 'medium', esrs: 'S3',
        detail: 'Charging accessibility, rural/urban equity, affordable EV availability' },
      
      // Consumer Safety (S4)
      { id: 'AUTO.20', name: 'Vehicle safety & recalls', priority: 'critical', esrs: 'S4',
        detail: 'Safety ratings, recall management, autonomous driving safety validation' },
      { id: 'AUTO.21', name: 'Data privacy & cybersecurity', priority: 'high', esrs: 'S4',
        detail: 'Connected vehicle data protection, OTA update security, user consent' },
      
      // Governance (G1)
      { id: 'AUTO.22', name: 'Emissions compliance & transparency', priority: 'critical', esrs: 'G1',
        detail: 'Regulatory compliance, emissions testing integrity, disclosure accuracy' },
      { id: 'AUTO.23', name: 'Lobbying & public policy', priority: 'high', esrs: 'G1',
        detail: 'Climate policy engagement, trade association positions, EV incentive advocacy' },
      { id: 'AUTO.24', name: 'EU Taxonomy alignment', priority: 'high', esrs: 'G1',
        detail: 'Taxonomy-eligible revenue, CapEx, OpEx; low-carbon vehicle manufacturing criteria' },
      { id: 'AUTO.25', name: 'Anti-corruption & fair competition', priority: 'medium', esrs: 'G1',
        detail: 'Supplier relationships, dealer network practices, competitive behavior' }
    ]
  },
  electric_utilities: {
    id: 'gri_eu',
    name: 'Electric Utilities',
    icon: Zap,
    color: 'yellow',
    description: 'GRI Electric Utilities Sector Standard 2024 - Energy Transition',
    topics: [
      // Climate & Energy (E1)
      { id: 'EU.1', name: 'GHG emissions from power generation', priority: 'critical', esrs: 'E1',
        detail: 'Scope 1 emissions from fossil fuel generation, emissions intensity (gCO2/kWh), fleet decarbonization' },
      { id: 'EU.2', name: 'Energy mix & renewable share', priority: 'critical', esrs: 'E1',
        detail: 'Generation by source (coal, gas, nuclear, hydro, wind, solar), renewable energy percentage, capacity additions' },
      { id: 'EU.3', name: 'Grid transmission losses', priority: 'high', esrs: 'E1',
        detail: 'Technical and non-technical losses, grid efficiency improvements, smart grid investments' },
      { id: 'EU.4', name: 'Coal phase-out strategy', priority: 'critical', esrs: 'E1',
        detail: 'Coal plant retirement schedule, stranded asset management, just transition for coal regions' },
      { id: 'EU.5', name: 'Methane emissions (gas operations)', priority: 'high', esrs: 'E1',
        detail: 'Fugitive emissions from gas infrastructure, leak detection programs, methane intensity' },
      
      // Pollution & Air Quality (E2)
      { id: 'EU.6', name: 'Air pollutants from generation', priority: 'high', esrs: 'E2',
        detail: 'NOx, SOx, PM emissions from thermal plants, emission control technologies, regulatory compliance' },
      { id: 'EU.7', name: 'Nuclear waste management', priority: 'critical', esrs: 'E2',
        detail: 'Spent fuel storage, decommissioning plans, long-term waste disposal solutions' },
      
      // Water (E3)
      { id: 'EU.8', name: 'Water consumption for cooling', priority: 'high', esrs: 'E3',
        detail: 'Water withdrawal by thermal plants, cooling technology (once-through vs closed-loop), water stress assessment' },
      { id: 'EU.9', name: 'Hydropower water impacts', priority: 'medium', esrs: 'E3',
        detail: 'River flow management, fish passage, downstream ecosystem effects, sediment transport' },
      
      // Biodiversity (E4)
      { id: 'EU.10', name: 'Land use for energy infrastructure', priority: 'medium', esrs: 'E4',
        detail: 'Solar/wind farm footprint, transmission corridor impacts, habitat fragmentation' },
      { id: 'EU.11', name: 'Wildlife impacts (wind/transmission)', priority: 'high', esrs: 'E4',
        detail: 'Bird and bat mortality at wind farms, electrocution risks on power lines, mitigation measures' },
      
      // Circular Economy (E5)
      { id: 'EU.12', name: 'Solar panel & battery recycling', priority: 'high', esrs: 'E5',
        detail: 'End-of-life management for renewables, material recovery rates, extended producer responsibility' },
      { id: 'EU.13', name: 'Coal ash & byproduct management', priority: 'medium', esrs: 'E5',
        detail: 'Fly ash utilization, ash pond remediation, beneficial reuse programs' },
      
      // Workforce (S1)
      { id: 'EU.14', name: 'Just transition for fossil fuel workers', priority: 'critical', esrs: 'S1',
        detail: 'Reskilling programs, early retirement packages, regional economic diversification support' },
      { id: 'EU.15', name: 'Occupational health & safety', priority: 'high', esrs: 'S1',
        detail: 'Electrical safety, working at heights, contractor safety management' },
      { id: 'EU.16', name: 'Workforce diversity & inclusion', priority: 'medium', esrs: 'S1',
        detail: 'Gender diversity in technical roles, diversity targets, inclusive hiring practices' },
      
      // Supply Chain (S2)
      { id: 'EU.17', name: 'Critical mineral sourcing', priority: 'critical', esrs: 'S2',
        detail: 'Rare earths for wind turbines, lithium/cobalt for storage, supply chain due diligence' },
      { id: 'EU.18', name: 'Solar panel supply chain ethics', priority: 'high', esrs: 'S2',
        detail: 'Forced labor risks in polysilicon production, supply chain transparency, third-party audits' },
      
      // Communities (S3)
      { id: 'EU.19', name: 'Energy affordability & access', priority: 'critical', esrs: 'S3',
        detail: 'Energy poverty programs, low-income assistance, disconnection policies' },
      { id: 'EU.20', name: 'Community engagement for projects', priority: 'high', esrs: 'S3',
        detail: 'Stakeholder consultation for new infrastructure, benefit-sharing agreements, NIMBY management' },
      { id: 'EU.21', name: 'Indigenous rights & land use', priority: 'high', esrs: 'S3',
        detail: 'Free prior informed consent, sacred site protection, indigenous partnerships' },
      
      // Consumer (S4)
      { id: 'EU.22', name: 'Grid reliability & resilience', priority: 'critical', esrs: 'S4',
        detail: 'SAIDI/SAIFI metrics, outage management, climate resilience investments' },
      { id: 'EU.23', name: 'Cybersecurity of grid infrastructure', priority: 'critical', esrs: 'S4',
        detail: 'Critical infrastructure protection, NERC CIP compliance, incident response' },
      { id: 'EU.24', name: 'Smart meter data privacy', priority: 'medium', esrs: 'S4',
        detail: 'Customer data protection, consent management, third-party data sharing policies' },
      
      // Governance (G1)
      { id: 'EU.25', name: 'Regulatory compliance & rate cases', priority: 'high', esrs: 'G1',
        detail: 'PUC/FERC compliance, rate case proceedings, regulatory relationship management' },
      { id: 'EU.26', name: 'Climate lobbying & policy alignment', priority: 'high', esrs: 'G1',
        detail: 'Trade association positions on climate policy, Paris Agreement alignment, political contributions' },
      { id: 'EU.27', name: 'EU Taxonomy alignment (utilities)', priority: 'high', esrs: 'G1',
        detail: 'Taxonomy-eligible generation capacity, green bond issuance, sustainable finance framework' },
      { id: 'EU.28', name: 'Nuclear safety governance', priority: 'critical', esrs: 'G1',
        detail: 'NRC compliance, safety culture, emergency preparedness, public disclosure' }
    ]
  },
  textiles: {
    id: 'gri_tex',
    name: 'Textiles & Apparel',
    icon: Shirt,
    color: 'pink',
    description: 'GRI Textiles Sector Standard 2024 - Sustainable Fashion & Circular Economy',
    topics: [
      // Climate & Energy (E1)
      { id: 'TEX.1', name: 'Carbon footprint of production', priority: 'critical', esrs: 'E1',
        detail: 'GHG emissions from fiber production, dyeing, finishing, transportation. Scope 1-3 emissions intensity per garment' },
      { id: 'TEX.2', name: 'Energy use in manufacturing', priority: 'high', esrs: 'E1',
        detail: 'Energy consumption in spinning, weaving, dyeing. Renewable energy adoption in factories' },
      { id: 'TEX.3', name: 'Supply chain decarbonization', priority: 'critical', esrs: 'E1',
        detail: 'Tier 1-4 supplier emissions, raw material sourcing impacts, logistics optimization' },
      
      // Pollution & Chemicals (E2)
      { id: 'TEX.4', name: 'Chemical management & ZDHC', priority: 'critical', esrs: 'E2',
        detail: 'Restricted substances lists (RSL), ZDHC MRSL compliance, chemical inventory management, safer alternatives' },
      { id: 'TEX.5', name: 'Wastewater discharge', priority: 'critical', esrs: 'E2',
        detail: 'Dyeing effluent treatment, ZDHC wastewater guidelines, heavy metals, pH, COD/BOD levels' },
      { id: 'TEX.6', name: 'Microplastics & fiber shedding', priority: 'high', esrs: 'E2',
        detail: 'Synthetic fiber microplastic release during washing, fiber fragmentation, filtration solutions' },
      { id: 'TEX.7', name: 'Air emissions from processing', priority: 'medium', esrs: 'E2',
        detail: 'VOCs from printing/coating, formaldehyde emissions, particulate matter from cutting' },
      
      // Water (E3)
      { id: 'TEX.8', name: 'Water consumption in production', priority: 'critical', esrs: 'E3',
        detail: 'Water usage in dyeing/finishing (50-200 liters per kg fabric), cotton irrigation, water stress mapping' },
      { id: 'TEX.9', name: 'Water stewardship programs', priority: 'high', esrs: 'E3',
        detail: 'Watershed protection, water recycling systems, zero liquid discharge (ZLD) adoption' },
      
      // Biodiversity (E4)
      { id: 'TEX.10', name: 'Raw material sourcing impacts', priority: 'high', esrs: 'E4',
        detail: 'Cotton farming biodiversity loss, viscose/lyocell forest sourcing, animal-derived materials (leather, wool, down)' },
      { id: 'TEX.11', name: 'Land use & deforestation', priority: 'high', esrs: 'E4',
        detail: 'Deforestation-free viscose commitments, sustainable forestry certifications (FSC/PEFC), leather traceability' },
      
      // Circular Economy (E5)
      { id: 'TEX.12', name: 'Textile waste & recycling', priority: 'critical', esrs: 'E5',
        detail: 'Pre/post-consumer textile waste, fiber-to-fiber recycling rates, mechanical vs chemical recycling' },
      { id: 'TEX.13', name: 'Design for circularity', priority: 'critical', esrs: 'E5',
        detail: 'Mono-material design, recyclability assessment, durability standards, repair/refurbishment programs' },
      { id: 'TEX.14', name: 'Recycled content in products', priority: 'high', esrs: 'E5',
        detail: 'Recycled polyester (rPET), recycled cotton, Global Recycled Standard (GRS) certification' },
      { id: 'TEX.15', name: 'Take-back & resale programs', priority: 'high', esrs: 'E5',
        detail: 'Brand take-back schemes, resale platforms, rental/subscription models, upcycling initiatives' },
      { id: 'TEX.16', name: 'Packaging sustainability', priority: 'medium', esrs: 'E5',
        detail: 'Plastic-free packaging, recycled/recyclable materials, minimalist packaging design' },
      
      // Workforce - Own Operations (S1)
      { id: 'TEX.17', name: 'Living wages in factories', priority: 'critical', esrs: 'S1',
        detail: 'Living wage benchmarks vs minimum wage, wage ladder progression, fair compensation practices' },
      { id: 'TEX.18', name: 'Working hours & overtime', priority: 'critical', esrs: 'S1',
        detail: 'Excessive overtime prevention, seasonal production spikes, workforce planning' },
      { id: 'TEX.19', name: 'Factory health & safety', priority: 'critical', esrs: 'S1',
        detail: 'Building safety (post-Rana Plaza), fire safety, chemical handling, ergonomics, PPE provision' },
      { id: 'TEX.20', name: 'Freedom of association', priority: 'high', esrs: 'S1',
        detail: 'Union rights, collective bargaining agreements, worker representation mechanisms' },
      
      // Supply Chain Workers (S2)
      { id: 'TEX.21', name: 'Forced labor & modern slavery', priority: 'critical', esrs: 'S2',
        detail: 'Debt bondage risks, migrant worker exploitation, recruitment fee reimbursement, Xinjiang cotton risks' },
      { id: 'TEX.22', name: 'Child labor prevention', priority: 'critical', esrs: 'S2',
        detail: 'Age verification systems, remediation programs, education support, family income interventions' },
      { id: 'TEX.23', name: 'Supply chain transparency', priority: 'critical', esrs: 'S2',
        detail: 'Tier 1-4 supplier mapping, factory disclosure, Open Apparel Registry participation' },
      { id: 'TEX.24', name: 'Supplier audit & remediation', priority: 'high', esrs: 'S2',
        detail: 'Social compliance audits, corrective action plans, capacity building programs, beyond-audit approaches' },
      { id: 'TEX.25', name: 'Homeworker & informal labor', priority: 'high', esrs: 'S2',
        detail: 'Informal sector visibility, fair wages for homeworkers, handloom/artisan partnerships' },
      
      // Communities (S3)
      { id: 'TEX.26', name: 'Community impact of factories', priority: 'medium', esrs: 'S3',
        detail: 'Local employment, community investment, infrastructure development, pollution impacts on communities' },
      { id: 'TEX.27', name: 'Artisan & craft preservation', priority: 'medium', esrs: 'S3',
        detail: 'Traditional textile techniques, artisan livelihoods, cultural heritage protection, fair trade partnerships' },
      
      // Consumers (S4)
      { id: 'TEX.28', name: 'Product safety & quality', priority: 'high', esrs: 'S4',
        detail: 'Chemical safety for consumers, allergen management, durability/quality standards, recall procedures' },
      { id: 'TEX.29', name: 'Sustainable consumption education', priority: 'medium', esrs: 'S4',
        detail: 'Care label transparency, garment longevity guidance, repair tutorials, environmental impact labeling' },
      { id: 'TEX.30', name: 'Marketing & greenwashing', priority: 'high', esrs: 'S4',
        detail: 'Substantiation of sustainability claims, Green Claims Directive compliance, third-party verification' },
      
      // Governance (G1)
      { id: 'TEX.31', name: 'Supply chain due diligence', priority: 'critical', esrs: 'G1',
        detail: 'EU CSDDD compliance, human rights due diligence, environmental due diligence, grievance mechanisms' },
      { id: 'TEX.32', name: 'Certification & standards', priority: 'high', esrs: 'G1',
        detail: 'GOTS, OEKO-TEX, Bluesign, Fair Trade, B Corp certification, multi-stakeholder initiatives (SAC, ZDHC)' },
      { id: 'TEX.33', name: 'Traceability systems', priority: 'high', esrs: 'G1',
        detail: 'Blockchain traceability, fiber tracking (e.g., TextileGenesis), chain of custody documentation' },
      { id: 'TEX.34', name: 'EU regulations compliance', priority: 'critical', esrs: 'G1',
        detail: 'EU Strategy for Sustainable Textiles, Digital Product Passport (DPP), Extended Producer Responsibility (EPR)' },
      { id: 'TEX.35', name: 'Industry collaboration', priority: 'medium', esrs: 'G1',
        detail: 'Fashion Pact commitments, Science Based Targets initiative, Ellen MacArthur Foundation network' }
    ]
  },
  it_technology: {
    id: 'gri_it',
    name: 'IT & Technology',
    icon: Monitor,
    color: 'cyan',
    description: 'GRI Technology Sector Standard 2024 - Digital Sustainability & Responsible Tech',
    topics: [
      // Climate & Energy (E1)
      { id: 'IT.1', name: 'Data center energy consumption', priority: 'critical', esrs: 'E1',
        detail: 'PUE (Power Usage Effectiveness), server utilization rates, cooling efficiency, renewable energy procurement for data centers' },
      { id: 'IT.2', name: 'Cloud services carbon footprint', priority: 'critical', esrs: 'E1',
        detail: 'Scope 3 emissions from cloud computing, carbon intensity per compute hour, green cloud certifications' },
      { id: 'IT.3', name: 'AI/ML training emissions', priority: 'high', esrs: 'E1',
        detail: 'Carbon footprint of large language model training, GPU energy consumption, model efficiency optimization' },
      { id: 'IT.4', name: 'Office & facilities emissions', priority: 'medium', esrs: 'E1',
        detail: 'Remote work impact, office building efficiency, business travel reduction, commuting emissions' },
      { id: 'IT.5', name: 'Network infrastructure energy', priority: 'high', esrs: 'E1',
        detail: '5G vs 4G efficiency, fiber optic vs copper, edge computing energy optimization' },
      
      // Pollution & E-Waste (E2)
      { id: 'IT.6', name: 'Electronic waste management', priority: 'critical', esrs: 'E2',
        detail: 'E-waste recycling rates, responsible disposal partnerships, take-back programs, Basel Convention compliance' },
      { id: 'IT.7', name: 'Hazardous materials in electronics', priority: 'high', esrs: 'E2',
        detail: 'Lead-free soldering, RoHS compliance, brominated flame retardants, mercury in displays' },
      { id: 'IT.8', name: 'Battery disposal & recycling', priority: 'high', esrs: 'E2',
        detail: 'Lithium-ion battery recycling, cobalt recovery, battery collection programs, second-life applications' },
      
      // Water (E3)
      { id: 'IT.9', name: 'Data center water usage', priority: 'high', esrs: 'E3',
        detail: 'WUE (Water Usage Effectiveness), cooling tower water consumption, water-free cooling alternatives' },
      { id: 'IT.10', name: 'Semiconductor manufacturing water', priority: 'medium', esrs: 'E3',
        detail: 'Ultra-pure water requirements, wastewater treatment, water recycling in chip fabrication' },
      
      // Biodiversity (E4)
      { id: 'IT.11', name: 'Rare earth mining impacts', priority: 'high', esrs: 'E4',
        detail: 'Biodiversity loss from mining operations, habitat destruction, tailings pond management' },
      { id: 'IT.12', name: 'Infrastructure land use', priority: 'medium', esrs: 'E4',
        detail: 'Data center site selection, solar farm impacts, undersea cable environmental assessment' },
      
      // Circular Economy (E5)
      { id: 'IT.13', name: 'Product longevity & repairability', priority: 'critical', esrs: 'E5',
        detail: 'Right to repair compliance, spare parts availability, software update lifespan, planned obsolescence' },
      { id: 'IT.14', name: 'Recycled materials in products', priority: 'high', esrs: 'E5',
        detail: 'Recycled aluminum/plastic content, conflict-free minerals, certified recycled rare earths' },
      { id: 'IT.15', name: 'Refurbishment & resale programs', priority: 'high', esrs: 'E5',
        detail: 'Certified refurbished devices, trade-in programs, enterprise equipment lifecycle extension' },
      { id: 'IT.16', name: 'Packaging sustainability', priority: 'medium', esrs: 'E5',
        detail: 'Plastic-free packaging, right-sized boxes, recycled materials, carbon-neutral shipping' },
      
      // Own Workforce (S1)
      { id: 'IT.17', name: 'Tech worker well-being', priority: 'high', esrs: 'S1',
        detail: 'Burnout prevention, work-life balance, mental health support, on-call policies, crunch time practices' },
      { id: 'IT.18', name: 'Diversity in tech', priority: 'critical', esrs: 'S1',
        detail: 'Gender diversity in engineering, racial/ethnic representation, LGBTQ+ inclusion, disability hiring, pay equity' },
      { id: 'IT.19', name: 'Contractor & gig worker treatment', priority: 'high', esrs: 'S1',
        detail: 'Contractor benefits, temp worker rights, misclassification risks, vendor worker policies' },
      { id: 'IT.20', name: 'AI workforce displacement', priority: 'high', esrs: 'S1',
        detail: 'Reskilling programs for automation-affected workers, internal mobility, just transition planning' },
      
      // Supply Chain Workers (S2)
      { id: 'IT.21', name: 'Electronics manufacturing labor', priority: 'critical', esrs: 'S2',
        detail: 'Factory worker conditions, overtime limits, dormitory standards, RBA (Responsible Business Alliance) audits' },
      { id: 'IT.22', name: 'Conflict minerals due diligence', priority: 'critical', esrs: 'S2',
        detail: '3TG (tin, tantalum, tungsten, gold) sourcing, DRC conflict-free certification, SEC disclosure compliance' },
      { id: 'IT.23', name: 'Cobalt supply chain ethics', priority: 'critical', esrs: 'S2',
        detail: 'Artisanal mining child labor risks, DRC cobalt traceability, Fair Cobalt Alliance membership' },
      { id: 'IT.24', name: 'Content moderation worker welfare', priority: 'high', esrs: 'S2',
        detail: 'PTSD support for content moderators, outsourced moderation conditions, mental health resources' },
      
      // Communities (S3)
      { id: 'IT.25', name: 'Digital divide & access', priority: 'critical', esrs: 'S3',
        detail: 'Affordable internet access, device affordability programs, digital literacy initiatives, rural connectivity' },
      { id: 'IT.26', name: 'Community broadband impact', priority: 'medium', esrs: 'S3',
        detail: 'Local hiring for infrastructure projects, community benefit agreements, small business digitization support' },
      { id: 'IT.27', name: 'Tech hub gentrification', priority: 'medium', esrs: 'S3',
        detail: 'Housing affordability near campuses, community displacement, local investment programs' },
      
      // Consumers & Users (S4)
      { id: 'IT.28', name: 'Data privacy & protection', priority: 'critical', esrs: 'S4',
        detail: 'GDPR/CCPA compliance, data minimization, consent management, third-party data sharing, breach notification' },
      { id: 'IT.29', name: 'Cybersecurity & user safety', priority: 'critical', esrs: 'S4',
        detail: 'Security by design, vulnerability disclosure, encryption standards, incident response, ransomware protection' },
      { id: 'IT.30', name: 'AI ethics & algorithmic bias', priority: 'critical', esrs: 'S4',
        detail: 'Fairness in AI systems, bias testing, explainability, EU AI Act compliance, high-risk AI governance' },
      { id: 'IT.31', name: 'Misinformation & platform safety', priority: 'critical', esrs: 'S4',
        detail: 'Content moderation policies, fact-checking partnerships, election integrity, child safety (CSAM prevention)' },
      { id: 'IT.32', name: 'Digital addiction & well-being', priority: 'high', esrs: 'S4',
        detail: 'Screen time features, engagement design ethics, teen mental health impact, dark patterns prohibition' },
      { id: 'IT.33', name: 'Accessibility compliance', priority: 'high', esrs: 'S4',
        detail: 'WCAG compliance, assistive technology support, inclusive design, ADA/EAA requirements' },
      
      // Governance (G1)
      { id: 'IT.34', name: 'Antitrust & market competition', priority: 'high', esrs: 'G1',
        detail: 'Market dominance practices, acquisition scrutiny, platform neutrality, interoperability requirements' },
      { id: 'IT.35', name: 'Tax transparency', priority: 'high', esrs: 'G1',
        detail: 'Country-by-country reporting, transfer pricing, tax haven usage, digital services taxes' },
      { id: 'IT.36', name: 'Political influence & lobbying', priority: 'high', esrs: 'G1',
        detail: 'Lobbying disclosure, political contributions, trade association positions, revolving door policies' },
      { id: 'IT.37', name: 'AI governance & risk management', priority: 'critical', esrs: 'G1',
        detail: 'AI ethics boards, model risk management, algorithmic accountability, third-party AI audits' },
      { id: 'IT.38', name: 'Open source & IP practices', priority: 'medium', esrs: 'G1',
        detail: 'Open source contributions, patent pledges, interoperability standards, developer ecosystem fairness' },
      { id: 'IT.39', name: 'Government surveillance requests', priority: 'high', esrs: 'G1',
        detail: 'Transparency reports, lawful access policies, encryption backdoor stance, human rights impact assessment' },
      { id: 'IT.40', name: 'Supply chain transparency', priority: 'high', esrs: 'G1',
        detail: 'Supplier list disclosure, audit results publication, corrective action reporting, conflict minerals filings' }
    ]
  }
};

// Sector Card Component
const SectorCard = ({ sector, sectorKey, onSelect, isSelected }) => {
  const IconComponent = sector.icon;
  const colorClasses = {
    amber: 'bg-amber-100 text-amber-600 border-amber-200',
    slate: 'bg-slate-100 text-slate-600 border-slate-200',
    green: 'bg-green-100 text-green-600 border-green-200',
    orange: 'bg-orange-100 text-orange-600 border-orange-200',
    blue: 'bg-blue-100 text-blue-600 border-blue-200',
    indigo: 'bg-indigo-100 text-indigo-600 border-indigo-200',
    yellow: 'bg-yellow-100 text-yellow-600 border-yellow-200',
    pink: 'bg-pink-100 text-pink-600 border-pink-200',
    cyan: 'bg-cyan-100 text-cyan-600 border-cyan-200'
  };
  
  return (
    <div
      onClick={() => onSelect(sectorKey)}
      className={`p-5 rounded-xl border-2 cursor-pointer transition-all hover:shadow-lg ${
        isSelected 
          ? `border-${sector.color}-500 bg-${sector.color}-50` 
          : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
      data-testid={`sector-${sectorKey}`}
    >
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-xl ${colorClasses[sector.color]}`}>
          <IconComponent size={28} />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-slate-900">{sector.name}</h3>
          <p className="text-xs text-slate-500">{sector.description}</p>
          <p className="text-sm text-slate-600 mt-1">{sector.topics.length} material topics</p>
        </div>
        <ChevronRight size={20} className={`text-slate-400 ${isSelected ? 'rotate-90' : ''}`} />
      </div>
    </div>
  );
};

// Topic Priority Badge
const PriorityBadge = ({ priority }) => {
  const colors = {
    critical: 'bg-red-100 text-red-700 border-red-200',
    high: 'bg-amber-100 text-amber-700 border-amber-200',
    medium: 'bg-blue-100 text-blue-700 border-blue-200',
    low: 'bg-slate-100 text-slate-600 border-slate-200'
  };
  
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${colors[priority]}`}>
      {priority.toUpperCase()}
    </span>
  );
};

// Sector Topics Component
const SectorTopics = ({ sector }) => {
  const [filter, setFilter] = useState('all');
  
  const filteredTopics = sector.topics.filter(topic => {
    if (filter === 'all') return true;
    return topic.priority === filter;
  });

  const priorityCounts = {
    critical: sector.topics.filter(t => t.priority === 'critical').length,
    high: sector.topics.filter(t => t.priority === 'high').length,
    medium: sector.topics.filter(t => t.priority === 'medium').length
  };

  return (
    <div className="space-y-6">
      {/* Sector Header */}
      <div className={`bg-gradient-to-r from-${sector.color}-50 to-${sector.color}-100 rounded-xl p-6 border border-${sector.color}-200`}>
        <h3 className="font-bold text-xl text-slate-900 mb-2">{sector.name}</h3>
        <p className="text-slate-600 text-sm mb-4">{sector.description}</p>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-red-600">{priorityCounts.critical}</p>
            <p className="text-xs text-slate-500">Critical</p>
          </div>
          <div className="bg-white rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-amber-600">{priorityCounts.high}</p>
            <p className="text-xs text-slate-500">High Priority</p>
          </div>
          <div className="bg-white rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-blue-600">{priorityCounts.medium}</p>
            <p className="text-xs text-slate-500">Medium</p>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-600">Filter:</span>
        {['all', 'critical', 'high', 'medium'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              filter === f 
                ? 'bg-slate-800 text-white' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Topics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredTopics.map((topic) => (
          <div 
            key={topic.id}
            className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow"
            data-testid={`topic-${topic.id}`}
          >
            <div className="flex items-start justify-between mb-2">
              <span className="font-mono text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                {topic.id}
              </span>
              <PriorityBadge priority={topic.priority} />
            </div>
            <h4 className="font-medium text-slate-900 mb-2">{topic.name}</h4>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">ESRS Mapping:</span>
              <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">
                {topic.esrs}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* ESRS Alignment */}
      <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
        <h4 className="font-bold text-slate-900 mb-3">ESRS Alignment Summary</h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {['E1', 'E2', 'E3', 'E4', 'E5', 'S1', 'S2', 'S3', 'S4', 'G1'].map(esrs => {
            const count = sector.topics.filter(t => t.esrs === esrs).length;
            return (
              <div key={esrs} className="bg-white rounded-lg p-2 text-center">
                <span className="text-sm font-bold text-slate-700">{esrs}</span>
                <p className="text-lg font-bold text-emerald-600">{count}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Main GRI Sectors Page
const GRISectors = () => {
  const [selectedSector, setSelectedSector] = useState(null);
  const [exporting, setExporting] = useState(false);

  const handleExportPDF = async () => {
    if (!selectedSector) return;
    
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const sector = GRI_SECTOR_STANDARDS[selectedSector];
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/gri-sector/pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sector_id: selectedSector,
          sector_name: sector.name,
          description: sector.description,
          topics: sector.topics
        })
      });
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `gri_${selectedSector}_report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export PDF failed:', error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="gri-sectors-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 rounded-xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <h1 className="text-3xl font-bold">GRI Sector Standards</h1>
              <span className="px-2 py-1 bg-amber-400 text-amber-900 text-xs font-bold rounded-full flex items-center gap-1">
                <Crown size={12} /> PREMIUM
              </span>
            </div>
            <p className="text-purple-200">
              Sector-specific material topics aligned with ESRS disclosure requirements
            </p>
          </div>
          <div className="flex items-center gap-4">
            {selectedSector && (
              <button
                onClick={handleExportPDF}
                disabled={exporting}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50"
                data-testid="export-sector-pdf-btn"
              >
                {exporting ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  <Download size={16} />
                )}
                Export PDF
              </button>
            )}
            <div className="text-right">
              <p className="text-sm text-purple-200">Available Sectors</p>
              <p className="text-3xl font-bold">{Object.keys(GRI_SECTOR_STANDARDS).length}</p>
            </div>
          </div>
        </div>
      </div>

      <PremiumGate featureKey="gri-sectors">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sectors List */}
          <div className="lg:col-span-1 space-y-3">
            <h3 className="font-bold text-slate-900">Select Your Sector</h3>
            <div className="space-y-3">
              {Object.entries(GRI_SECTOR_STANDARDS).map(([key, sector]) => (
                <SectorCard
                  key={key}
                  sector={sector}
                  sectorKey={key}
                  onSelect={setSelectedSector}
                  isSelected={selectedSector === key}
                />
              ))}
            </div>
          </div>

          {/* Sector Detail */}
          <div className="lg:col-span-2">
            {selectedSector ? (
              <SectorTopics sector={GRI_SECTOR_STANDARDS[selectedSector]} />
            ) : (
              <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                <Globe size={48} className="mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">Select a Sector</h3>
                <p className="text-sm text-slate-500">
                  Choose a sector from the list to view material topics and ESRS mappings
                </p>
              </div>
            )}
          </div>
        </div>
      </PremiumGate>
    </div>
  );
};

export default GRISectors;
